import React, { useState, useEffect } from 'react';
import { vaultAPI } from '../services/api';

/**
 * PCI Vault card capture component.
 *
 * In sandbox mode, shows a simple form that simulates PCI Vault's PCD hosted form.
 * In production, would load the actual PCD form JS from pcivault.io.
 *
 * Card data NEVER passes through our servers — the PCD form submits directly
 * to PCI Vault, which returns a vault token to our callback.
 */
function CardCapture({ clubId, onSuccess, onError }) {
  const [loading, setLoading] = useState(false);
  const [cardStatus, setCardStatus] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    cardNumber: '',
    cardExpiry: '',
    cardCvc: '',
    cardName: '',
  });

  useEffect(() => {
    checkCardStatus();
  }, [clubId]);

  const checkCardStatus = async () => {
    try {
      const data = await vaultAPI.getStatus(clubId);
      if (data.success) {
        setCardStatus(data);
      }
    } catch (err) {
      // No card stored yet
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Step 1: Create capture session
      const session = await vaultAPI.createSession(clubId);
      if (!session.success) {
        onError?.(session.error || 'Failed to create capture session');
        return;
      }

      // Step 2: In sandbox, simulate PCI Vault tokenization
      // In production, the PCD hosted form would handle this
      const lastFour = formData.cardNumber.replace(/\s/g, '').slice(-4);
      const simulatedToken = 'sandbox_vault_' + Date.now() + '_' + lastFour;

      // Detect card type from first digit
      const firstDigit = formData.cardNumber.replace(/\s/g, '')[0];
      const cardType = firstDigit === '4' ? 'Visa' :
                       firstDigit === '5' ? 'Mastercard' :
                       firstDigit === '3' ? 'Amex' : 'Unknown';

      // Step 3: Confirm token with our backend
      const result = await vaultAPI.confirmToken(clubId, {
        vault_token: simulatedToken,
        vault_reference: session.reference,
        card_last_four: lastFour,
        card_type: cardType,
        card_expiry: formData.cardExpiry,
      });

      if (result.success) {
        setCardStatus({
          has_card: true,
          card_last_four: lastFour,
          card_type: cardType,
          card_expiry: formData.cardExpiry,
        });
        setShowForm(false);
        setFormData({ cardNumber: '', cardExpiry: '', cardCvc: '', cardName: '' });
        onSuccess?.(result);
      } else {
        onError?.(result.error || 'Failed to save card');
      }
    } catch (err) {
      onError?.(err.response?.data?.error || 'Failed to save card');
    } finally {
      setLoading(false);
    }
  };

  const formatCardNumber = (value) => {
    const v = value.replace(/\D/g, '').slice(0, 16);
    return v.replace(/(.{4})/g, '$1 ').trim();
  };

  const formatExpiry = (value) => {
    const v = value.replace(/\D/g, '').slice(0, 4);
    if (v.length >= 3) return v.slice(0, 2) + '/' + v.slice(2);
    return v;
  };

  if (cardStatus?.has_card && !showForm) {
    return (
      <div className="card-capture">
        <div className="card-stored">
          <div className="card-icon">
            <i className="fas fa-credit-card"></i>
          </div>
          <div className="card-info">
            <span className="card-type">{cardStatus.card_type || 'Card'}</span>
            <span className="card-number">**** **** **** {cardStatus.card_last_four}</span>
            {cardStatus.card_expiry && (
              <span className="card-expiry">Expires {cardStatus.card_expiry}</span>
            )}
          </div>
          <button
            type="button"
            className="card-change-btn"
            onClick={() => setShowForm(true)}
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card-capture">
      {!showForm && !cardStatus?.has_card && (
        <button
          type="button"
          className="card-add-btn"
          onClick={() => setShowForm(true)}
        >
          <i className="fas fa-plus-circle"></i> Add Payment Card
        </button>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="card-form">
          <div className="card-form-header">
            <h4>
              <i className="fas fa-lock"></i> Secure Card Entry
            </h4>
            <p>Card data is securely handled by PCI Vault. We never see your full card number.</p>
          </div>

          <div className="card-form-field">
            <label>Cardholder Name</label>
            <input
              type="text"
              value={formData.cardName}
              onChange={(e) => setFormData({...formData, cardName: e.target.value})}
              placeholder="John Smith"
              required
            />
          </div>

          <div className="card-form-field">
            <label>Card Number</label>
            <input
              type="text"
              value={formData.cardNumber}
              onChange={(e) => setFormData({...formData, cardNumber: formatCardNumber(e.target.value)})}
              placeholder="4111 1111 1111 1111"
              maxLength={19}
              required
            />
          </div>

          <div className="card-form-row">
            <div className="card-form-field">
              <label>Expiry</label>
              <input
                type="text"
                value={formData.cardExpiry}
                onChange={(e) => setFormData({...formData, cardExpiry: formatExpiry(e.target.value)})}
                placeholder="MM/YY"
                maxLength={5}
                required
              />
            </div>
            <div className="card-form-field">
              <label>CVC</label>
              <input
                type="text"
                value={formData.cardCvc}
                onChange={(e) => setFormData({...formData, cardCvc: e.target.value.replace(/\D/g, '').slice(0, 4)})}
                placeholder="123"
                maxLength={4}
                required
              />
            </div>
          </div>

          <div className="card-form-actions">
            <button type="submit" className="card-save-btn" disabled={loading}>
              {loading ? 'Saving...' : 'Save Card'}
            </button>
            <button
              type="button"
              className="card-cancel-btn"
              onClick={() => setShowForm(false)}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default CardCapture;
